import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import axiosInstance from '@/lib/axios'
import { impostaStatoSimulazione } from '@/lib/statoSimulazione'

/**
 * SimulazioneContext — "vedi come <ruolo>"
 *
 * Tiene il ruolo simulato e i permessi che gli corrispondono, presi da
 * GET /permissions/matrix. Va montato SOPRA AuthProvider: e' AuthContext a
 * consumarlo e a esporre i permessi simulati al posto dei propri, cosi'
 * usePermission e usePermissions continuano a leggere da un punto solo
 * senza sapere che esiste una simulazione.
 *
 * La matrice si carica UNA volta e resta in memoria: la sua rotta richiede
 * system.manage_roles, che il ruolo simulato potrebbe non avere. Chiederla
 * durante la simulazione fallirebbe.
 *
 * Niente localStorage: ricaricare la pagina riporta l'utente a se stesso.
 *
 * Mentre la simulazione e' attiva l'applicazione va in SOLA LETTURA: lo stato
 * viene rispecchiato in lib/statoSimulazione, che l'interceptor axios legge
 * per fermare POST, PUT, PATCH e DELETE prima che partano.
 */

export interface RuoloSimulabile {
  id: number
  codice: string
  nome: string
}

// Rispecchia la risposta di GET /permissions/matrix, per la sola parte usata.
export interface PermessoMatrice {
  id: number
  codice: string
  nome: string
  categoria: string
  descrizione: string
  roles: { ruolo_codice: string; granted: boolean }[]
}

interface MatriceDati {
  roles: RuoloSimulabile[]
  permissions: PermessoMatrice[]
}

interface SimulazioneContextType {
  ruoloSimulato: RuoloSimulabile | null
  /** null quando la simulazione e' spenta: AuthContext usa allora i permessi veri */
  permessiSimulati: string[] | null
  /** Anagrafica completa dei permessi (nome, descrizione, categoria) dalla matrice */
  permessiMatrice: PermessoMatrice[]
  /** Permessi accesi "in prova" dal pannello: vivono solo in memoria */
  permessiInProva: string[]
  aggiungiProva: (codice: string) => void
  rimuoviProva: (codice: string) => void
  salvaProveNellaMatrice: () => Promise<number>
  ruoli: RuoloSimulabile[]
  matriceCaricata: boolean
  caricamento: boolean
  errore: string | null
  caricaMatrice: () => Promise<void>
  simula: (codiceRuolo: string) => void
  esci: () => void
}

export const SimulazioneContext = createContext<SimulazioneContextType | undefined>(undefined)

export const SimulazioneProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [matrice, setMatrice] = useState<MatriceDati | null>(null)
  const [ruoloSimulato, setRuoloSimulato] = useState<RuoloSimulabile | null>(null)
  const [caricamento, setCaricamento] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  // Permessi accesi dal pannello per vedere l'effetto: mai scritti, si
  // perdono uscendo dalla simulazione o ricaricando la pagina.
  const [permessiInProva, setPermessiInProva] = useState<string[]>([])

  const caricaMatrice = useCallback(async () => {
    if (matrice || caricamento) return
    try {
      setCaricamento(true)
      setErrore(null)
      const res = await axiosInstance.get<MatriceDati>('/permissions/matrix')
      setMatrice(res.data)
    } catch {
      setErrore('Impossibile caricare la matrice dei permessi.')
    } finally {
      setCaricamento(false)
    }
  }, [matrice, caricamento])

  // Stessa logica di DiagnosticaGatePage: i permessi granted del ruolo scelto.
  const permessiSimulati = useMemo<string[] | null>(() => {
    if (!matrice || !ruoloSimulato) return null
    const codici = new Set<string>()
    for (const perm of matrice.permissions) {
      const riga = perm.roles.find(r => r.ruolo_codice === ruoloSimulato.codice)
      if (riga?.granted) codici.add(perm.codice)
    }
    // Le prove del pannello si sommano ai permessi del ruolo simulato.
    for (const codice of permessiInProva) codici.add(codice)
    return Array.from(codici)
  }, [matrice, ruoloSimulato, permessiInProva])

  const simula = useCallback(
    (codiceRuolo: string) => {
      const ruolo = matrice?.roles.find(r => r.codice === codiceRuolo) ?? null
      setRuoloSimulato(ruolo)
      setPermessiInProva([])
    },
    [matrice],
  )

  const esci = useCallback(() => {
    setRuoloSimulato(null)
    setPermessiInProva([])
  }, [])

  const aggiungiProva = useCallback((codice: string) => {
    setPermessiInProva(prec => (prec.includes(codice) ? prec : [...prec, codice]))
  }, [])

  const rimuoviProva = useCallback((codice: string) => {
    setPermessiInProva(prec => prec.filter(c => c !== codice))
  }, [])

  // Scrive nella matrice i soli permessi in prova, per il solo ruolo simulato.
  // update_permissions_matrix fa upsert riga per riga: i ruoli e i permessi
  // assenti dal payload restano come sono.
  const salvaProveNellaMatrice = useCallback(async (): Promise<number> => {
    if (!matrice || !ruoloSimulato || permessiInProva.length === 0) return 0
    const perRuolo: Record<string, boolean> = {}
    for (const codice of permessiInProva) {
      const perm = matrice.permissions.find(p => p.codice === codice)
      if (perm) perRuolo[String(perm.id)] = true
    }
    const quanti = Object.keys(perRuolo).length
    if (quanti === 0) return 0
    await axiosInstance.put('/permissions/matrix', { [String(ruoloSimulato.id)]: perRuolo })
    return quanti
  }, [matrice, ruoloSimulato, permessiInProva])

  // L'interceptor axios non puo' usare gli hook: gli si passa lo stato da qui.
  // Finche' la simulazione e' attiva ogni scrittura viene fermata nel browser.
  useEffect(() => {
    impostaStatoSimulazione({
      attiva: ruoloSimulato !== null,
      ruolo: ruoloSimulato ? `${ruoloSimulato.nome} (${ruoloSimulato.codice})` : null,
    })
  }, [ruoloSimulato])

  const value = useMemo<SimulazioneContextType>(
    () => ({
      ruoloSimulato,
      permessiSimulati,
      permessiMatrice: matrice?.permissions ?? [],
      permessiInProva,
      aggiungiProva,
      rimuoviProva,
      salvaProveNellaMatrice,
      ruoli: matrice?.roles ?? [],
      matriceCaricata: matrice !== null,
      caricamento,
      errore,
      caricaMatrice,
      simula,
      esci,
    }),
    [
      ruoloSimulato,
      permessiSimulati,
      permessiInProva,
      aggiungiProva,
      rimuoviProva,
      salvaProveNellaMatrice,
      matrice,
      caricamento,
      errore,
      caricaMatrice,
      simula,
      esci,
    ],
  )

  return <SimulazioneContext.Provider value={value}>{children}</SimulazioneContext.Provider>
}

export const useSimulazione = (): SimulazioneContextType => {
  const context = useContext(SimulazioneContext)
  if (context === undefined) {
    throw new Error('useSimulazione must be used within a SimulazioneProvider')
  }
  return context
}
